import React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { AppUI } from '@seedlang/state';
import { Theme } from '@seedlang/constants';
import { ReviewHistoryTableWrapper } from '@seedlang/hoc';
import { isPresent } from '@seedlang/utils';
import { NeutralFaceIcon, SmileFaceIcon, BigSmileFaceIcon } from '@seedlang/icons';
import ProgressBar from 'components/progress_bar';
import Paginator from 'components/paginator';
import Spinner from 'components/spinner';

const ICONS = {
  Hard: <NeutralFaceIcon />,
  Good: <SmileFaceIcon />,
  Easy: <BigSmileFaceIcon />,
};

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: none !important;
  td {
    font-size: 12px;
    vertical-align: middle;
    padding: 4px 6px;
  }
  th {
    font-size: 11px;
    font-weight: bold;
    color: #777;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    padding: 4px 6px;
    text-align: left;
  }
`;

const Center = styled.div`
  display: flex;
  align-items: center;
`;

const IconWrapper = styled.div`
  margin-right: 5px;
  display: flex;
  align-items: center;
  .neutral-face-icon svg { fill: #656565; width: 16px; height: 16px; }
  .smile-face-icon svg, .big-smile-face-icon svg { fill: ${Theme.green}; width: 16px; height: 16px; }
`;

@observer
class ReviewHistoryTable extends React.Component {
  loadReviews() {
    AppUI.reviewStore.setLimit(20);
    AppUI.loadReviews(this.props.cardId);
  }

  componentDidMount() {
    if (this.props.cardId) {
      this.loadReviews();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.cardId !== this.props.cardId && this.props.cardId) {
      this.loadReviews();
    }
  }

  get rows() {
    const reviewRows = AppUI.reviewStore.indexData.filter(item => isPresent(item.repetitionInterval));
    return [...(this.props.testReviewRows || []), ...reviewRows];
  }

  get showPaginator() {
    const store = AppUI.reviewStore;
    return store.hasIndexData && (store.indexData.length >= store.limit || store.page !== 1);
  }

  get isLastPage() {
    const store = AppUI.reviewStore;
    const viewsCount = this.props.viewsCount;
    if (isPresent(viewsCount)) {
      return store.page >= Math.max(1, Math.ceil(viewsCount / store.limit));
    }
    return store.indexData.length < store.limit;
  }

  render() {
    if (AppUI.loadingReviews) {
      return <Spinner background={Theme.blue} />;
    }

    if (!AppUI.reviewStore.hasIndexData && !isPresent(this.props.testReviewRows)) {
      return (
        <p style={{color: '#777', fontSize: '13px', textAlign: 'center', padding: '20px 0'}}>
          You haven't reviewed this card yet.
        </p>
      );
    }

    return (
      <>
        <Table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Score</th>
              <th>Interval</th>
              <th>Progress</th>
            </tr>
          </thead>
          <tbody>
            {
              this.rows.map(item => {
                const repetitionIntervalValue = this.props.repetitionIntervalValue ? this.props.repetitionIntervalValue(item) : item.repetitionInterval;
                return (
                  <tr key={item.id}>
                    <td>
                      <Center>
                        {item.createdAtText || item.createdAt?.formattedDateWithYear}
                      </Center>
                    </td>
                    <td>
                      <Center>
                        <IconWrapper>
                          {ICONS[item.lastScoreText]}
                        </IconWrapper>
                        {item.lastScoreText}
                      </Center>
                    </td>
                    <td>
                      <Center>
                        {this.props.repetitionIntervalDisplay(item)}
                      </Center>
                    </td>
                    <td>
                      <ProgressBar
                        borderRadius='5px'
                        height='16px'
                        background='#CCC'
                        current={repetitionIntervalValue}
                        total={AppUI.user.daysUntilReviewRetired}
                      />
                    </td>
                  </tr>
                );
              })
            }
            {
              this.isLastPage &&
              <tr key='card-creation'>
                <td><Center>{this.props.creationDateText || ''}</Center></td>
                <td><Center>Creation</Center></td>
                <td><Center>-</Center></td>
                <td>
                  <ProgressBar
                    borderRadius='5px'
                    height='16px'
                    background='#CCC'
                    current={0}
                    total={AppUI.user.daysUntilReviewRetired}
                  />
                </td>
              </tr>
            }
          </tbody>
        </Table>
        {
          this.showPaginator &&
          <Paginator
            store={AppUI.reviewStore}
            ids={{ cardId: this.props.cardId }}
          />
        }
      </>
    );
  }
}

export default ReviewHistoryTableWrapper(ReviewHistoryTable);
